/** Tasks page: board / list / timeline views + detail modal. */
import { state } from "../state";
import { goTo } from "./shell";
import { openMeeting } from "./meeting";
import { deleteEvent } from "../data/store";
import { $, $$, byId, esc, on, toast } from "../lib/dom";
import { addDays, fmtRange, fmtShortDate, relativeDue, sameDay, startOfDay, startOfWeek } from "../lib/time";
import type { Task } from "../data/types";
import { completeTask, deleteTask, updateTask } from "../data/store";
import { scheduleTaskIds } from "./meeting";

/**
 * What a column means, so dropping a card into one is a real edit rather than a visual trick.
 *
 * Planned has no status of its own to set: a planned task is one with time on the calendar, so
 * dropping there asks Timo to find that time the same way the Find time button does.
 */
const COLUMN_STATUS: Record<string, "open" | "scheduled" | "done"> = { Inbox: "open", Planned: "scheduled", Done: "done" };

let filter = "all";
let search = "";
let activeTaskId: string | null = null;

const sourceLabel = (t: Task) => ({ user: "Manual", meeting: "Meeting", scheduler: "Timo", agent: "AI", calendar: "Calendar", extension: "Chrome" })[t.source];
const prioClass = (t: Task) => (t.priority >= 2 ? "high" : t.priority === 1 ? "medium" : "low");
const matches = (t: Task) => {
  const q = search.toLowerCase();
  if (q && !`${t.title} ${t.notes ?? ""}`.toLowerCase().includes(q)) return false;
  if (filter === "today") return !!t.dueAt && startOfDay(t.dueAt) <= startOfDay(new Date());
  if (filter === "meeting") return t.source === "meeting";
  if (filter === "ai") return t.source === "agent" || t.source === "scheduler";
  return true;
};

export function renderTasks(all: Task[]) {
  const open = all.filter((t) => t.status !== "done");
  $("#page-tasks .eyebrow")!.textContent = `Work queue · ${open.length} open`;
  const today = new Date();
  const dueToday = open.filter((t) => t.dueAt && startOfDay(t.dueAt) <= startOfDay(today));
  const week = open.filter((t) => t.dueAt && t.dueAt < addDays(startOfWeek(today), 7));
  const fromMeetings = open.filter((t) => t.source === "meeting");
  const doneRate = all.length ? Math.round((all.filter((t) => t.status === "done").length / all.length) * 100) : 0;
  const mins = week.reduce((a, t) => a + t.durationMin, 0);
  $("#page-tasks .metric-grid")!.innerHTML = [
    ["Due today", String(dueToday.length), `${dueToday.filter((t) => t.scheduledStart).length} already scheduled`],
    ["This week", String(week.length), `${Math.floor(mins / 60)}h ${mins % 60}m estimated`],
    ["From meetings", String(fromMeetings.length), `${fromMeetings.filter((t) => (t.confidence ?? 0) >= 0.7).length} high confidence`],
    ["Completed", `${doneRate}%`, `${all.filter((t) => t.status === "done").length} of ${all.length}`],
  ].map(([k, v, s]) => `<div class="card metric-card"><small>${k}</small><div class="metric-value">${v}</div><div class="metric-trend">${s}</div></div>`).join("");

  const visible = all.filter(matches).filter((t) => t.status !== "done" || filter === "all").filter((t) => t.status !== "suggested");
  renderSuggested(all.filter((t) => t.status === "suggested"));
  const card = (t: Task) => `<div class="board-task" data-task-id="${t.id}" data-source="${t.source}" data-status="${t.status}" tabindex="0" draggable="true" aria-grabbed="false"><b>${esc(t.title)}</b><p>${esc(t.notes || subtitle(t))}</p><div class="board-task-foot"><span class="tag">${sourceLabel(t)}</span><span class="tag">${t.scheduledStart ? fmtShortDate(t.scheduledStart) : relativeDue(t.dueAt)}</span></div></div>`;
  const cols: [string, Task[]][] = [
    ["Inbox", visible.filter((t) => t.status === "open")],
    ["Planned", visible.filter((t) => t.status === "scheduled")],
    ["Done", visible.filter((t) => t.status === "done")],
  ];
  $("#page-tasks .task-board")!.innerHTML = cols.map(([name, list]) => `<section class="board-column" data-drop="${COLUMN_STATUS[name]}"><div class="board-head"><b>${name}</b><span>${list.length}</span></div><div class="board-column-body">${list.map(card).join("") || (state.loaded.tasks ? `<p class="lead" style="font-size:13px;padding:6px 2px">Nothing here</p>` : `<p class="meta loading-line"><span class="loading-dot"></span>Loading your tasks…</p>`)}</div></section>`).join("");

  // List view, read like a mail client: one dense line per task, the things you do to it revealed
  // on hover, and a checkbox so several can be handled at once. A board is for seeing shape; a list
  // is for clearing a backlog, and clearing thirty things one dialog at a time is not clearing.
  const table = $("#page-tasks .task-table")!;
  const when = (t: Task) => t.scheduledStart
    ? `${relativeDue(t.scheduledStart)} · ${fmtRange(t.scheduledStart, t.scheduledEnd!).split(" – ")[0]}`
    : t.dueAt ? relativeDue(t.dueAt) : "Unscheduled";
  table.innerHTML =
    `<div class="task-bulk" id="taskBulk" hidden><label class="bulk-all"><input type="checkbox" id="taskSelectAll"><span id="taskBulkCount">0 selected</span></label><div class="bulk-actions"><button class="text-action" data-bulk="done">Mark done</button><button class="text-action" data-bulk="schedule">Find time</button><button class="text-action danger" data-bulk="delete">Delete</button></div></div>` +
    (visible.map((t) => `<div class="task-list-row${t.status === "done" ? " is-done" : ""}" data-task-id="${t.id}" data-source="${t.source}" tabindex="0">
        <input type="checkbox" class="row-check" data-select="${t.id}" aria-label="Select ${esc(t.title)}">
        <i class="priority-dot ${prioClass(t)}"></i>
        <span class="row-main"><b>${esc(t.title)}</b><small>${esc(t.notes || subtitle(t))}</small></span>
        <span class="row-source">${sourceLabel(t)}</span>
        <span class="row-when">${when(t)}</span>
        <span class="row-actions">
          ${t.status !== "done" ? `<button class="row-action" data-row="done" data-id="${t.id}" title="Mark done" aria-label="Mark done">✓</button>` : ""}
          ${t.status !== "done" && !t.scheduledStart ? `<button class="row-action" data-row="schedule" data-id="${t.id}" title="Find time" aria-label="Find time">◷</button>` : ""}
          <button class="row-action danger" data-row="delete" data-id="${t.id}" title="Delete" aria-label="Delete">🗑</button>
        </span>
      </div>`).join("") || `<p class="lead" style="padding:14px">No tasks match.</p>`);

  bindListActions();

  const monday = startOfWeek(today);
  const days = Array.from({ length: 5 }, (_, i) => addDays(monday, i));
  const planned = visible.filter((t) => t.scheduledStart);
  const totalMin = planned.reduce((a, t) => a + t.durationMin, 0);
  $("#page-tasks .timeline-head")!.innerHTML = `<div><b>${fmtShortDate(days[0]!)}–${days[4]!.getDate()}</b><span>${visible.length} tasks · ${Math.floor(totalMin / 60)}h ${totalMin % 60}m planned</span></div><button class="btn" id="findTimeAll">Find time with Timo</button>`;
  $("#page-tasks .timeline-grid")!.innerHTML = days.map((d) => {
    const list = planned.filter((t) => sameDay(t.scheduledStart!, d)).sort((a, b) => a.scheduledStart!.getTime() - b.scheduledStart!.getTime());
    const m = list.reduce((a, t) => a + t.durationMin, 0);
    return `<div class="timeline-day"><header><b>${["Mon", "Tue", "Wed", "Thu", "Fri"][(d.getDay() + 6) % 7]} ${d.getDate()}</b><span>${sameDay(d, today) ? "Today · " : ""}${m ? `${Math.floor(m / 60)}h ${m % 60}m` : "Open"}</span></header>${list.map((t) => `<button class="timeline-task${t.source === "agent" ? " ai-block" : ""}" data-task-id="${t.id}"><time>${fmtRange(t.scheduledStart!, t.scheduledEnd!)}</time><b>${esc(t.title)}</b><small>${t.durationMin}m · ${sourceLabel(t)}</small></button>`).join("") || `<div class="timeline-empty"><i>✦</i><b>Open</b><span>Timo can schedule a task here.</span></div>`}</div>`;
  }).join("");

  on(byId("findTimeAll"), "click", async () => {
    const ids = all.filter((t) => t.status === "open").map((t) => t.id);
    if (!ids.length) return toast("Nothing to schedule");
    await scheduleTaskIds(ids);
  });

  $$("#page-tasks [data-task-id]").forEach((el) => on(el, "click", () => openTaskDetail(all.find((t) => t.id === el.dataset.taskId)!)));
}

const subtitle = (t: Task) => (t.source === "meeting" ? `From a meeting${t.evidence ? ` · ${t.evidence.quote.slice(0, 60)}` : ""}` : `${t.durationMin} min estimate`);

const isoDay = (d: Date | null) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getMonth() >= 0 ? d.getDate() : 1).padStart(2, "0")}` : "");
const grow = (el: HTMLTextAreaElement) => { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; };

export function openTaskDetail(t: Task) {
  activeTaskId = t.id;
  const title = byId("taskDetailTitle") as HTMLTextAreaElement, notes = byId("taskDetailNotes") as HTMLTextAreaElement;
  title.value = t.title; notes.value = t.notes ?? "";
  (byId("taskDetailDue") as HTMLInputElement).value = isoDay(t.dueAt);
  (byId("taskDetailPriority") as HTMLSelectElement).value = String(t.priority ?? 1);
  (byId("taskDetailEffort") as HTMLInputElement).value = String(t.durationMin || 30);
  byId("taskDetailWhen")!.textContent = t.scheduledStart ? `${relativeDue(t.scheduledStart)} · ${fmtRange(t.scheduledStart, t.scheduledEnd ?? t.scheduledStart)}` : t.status === "done" ? "Done" : "Not yet, use Find time";
  const done = byId("completeDetailTask")!;
  done.classList.toggle("done", t.status === "done"); done.title = t.status === "done" ? "Reopen task" : "Mark complete";
  byId("taskDetailStatus")!.textContent = t.status === "done" ? "Done" : t.status === "suggested" ? "Awaiting approval" : t.scheduledStart ? "Planned" : "Open";
  const meeting = t.meetingId ? state.meetings.find((m) => m.id === t.meetingId) : null;
  // A task belongs to a project the way its meeting does, unless somebody says otherwise. People
  // were typing the project into the title - "Dako <> Provide training" - because there was
  // nowhere else to put it.
  const proj = byId<HTMLInputElement>("taskDetailProject")!;
  proj.value = t.project ?? meeting?.projects?.[0] ?? "";
  byId("taskProjectKnown")!.innerHTML = [...new Set(state.meetings.flatMap((m) => m.projects ?? []).concat(state.tasks.map((x) => x.project ?? "").filter(Boolean)))].map((n) => `<option value="${esc(n)}"></option>`).join("");
  byId("taskDetailSourceText")!.textContent = meeting ? `Captured in ${meeting.title} on ${meeting.startedAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}${t.confidence != null ? ` · ${Math.round(t.confidence * 100)}% confidence` : ""}.` : t.source === "agent" ? "Added by your connected AI assistant." : t.source === "meeting" ? "Captured from a meeting." : `Added by you on ${t.createdAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}.`;
  const q = byId("taskDetailQuote")!; q.hidden = !t.evidence?.quote; q.textContent = t.evidence?.quote ? `${t.evidence.quote}` : "";
  byId("taskDetailOpenSource")!.hidden = !t.meetingId;
  (byId("focusDetailTask") as HTMLButtonElement).hidden = t.status === "done";
  const modal = byId("taskDetailModal")!;
  modal.classList.add("show");
  modal.setAttribute("aria-hidden", "false");
  requestAnimationFrame(() => { grow(title); grow(notes); });
}
export function closeTaskDetail() {
  const modal = byId("taskDetailModal")!;
  modal.classList.remove("show");
  modal.setAttribute("aria-hidden", "true");
}

/**
 * What Timo heard in meetings, waiting for a yes or a no.
 *
 * This was a page of its own - the AI inbox - one click away from the tasks it was about to become.
 * It sits at the top of Tasks now, only when there is something to decide, and each row is the
 * decision: Add makes it a task, the cross lets it go.
 */
function renderSuggested(list: Task[]) {
  const host = byId("taskSuggested");
  if (!host) return;
  host.hidden = list.length === 0;
  if (!list.length) { host.innerHTML = ""; return; }
  const from = (t: Task) => {
    const m = t.meetingId ? state.meetings.find((x) => x.id === t.meetingId) : null;
    return m ? `From ${esc(m.title)}` : t.source === "agent" ? "From your AI assistant" : "Heard in a meeting";
  };
  host.innerHTML = `<div class="section-head"><div><h2>Suggested by Timo</h2><p class="meta">${list.length === 1 ? "One thing" : `${list.length} things`} you might have promised. Add the ones you did.</p></div>${list.length > 1 ? `<button class="text-action" id="suggestAddAll">Add all</button>` : ""}</div>
    <div class="suggest-list">${list.slice(0, 12).map((t) => `<div class="suggest-row" data-task-id="${t.id}">
      <span class="suggest-spark" aria-hidden="true">✦</span>
      <span class="suggest-copy"><b>${esc(t.title)}</b><small>${from(t)}${t.evidence?.quote ? ` · “${esc(t.evidence.quote.slice(0, 70))}”` : ""}</small></span>
      <button class="row-action" data-suggest="dismiss" data-id="${t.id}" title="Not a task" aria-label="Dismiss ${esc(t.title)}">✕</button>
      <button class="btn suggest-add" data-suggest="add" data-id="${t.id}">Add</button>
    </div>`).join("")}${list.length > 12 ? `<p class="meta" style="padding:8px 2px 0">${list.length - 12} more below these.</p>` : ""}</div>`;

  host.querySelectorAll<HTMLElement>("[data-suggest]").forEach((b) => on(b, "click", async (e) => {
    e.stopPropagation();
    const id = b.dataset.id!;
    b.closest<HTMLElement>(".suggest-row")?.classList.add("leaving");
    if (b.dataset.suggest === "add") { await updateTask(id, { status: "open" }, "user"); toast("Added to your tasks"); }
    else { await deleteTask(id); toast("Dismissed"); }
  }));
  on(byId("suggestAddAll"), "click", async () => {
    for (const t of list) await updateTask(t.id, { status: "open" }, "user");
    toast(`${list.length} added to your tasks`);
  });
}

/** Which rows are ticked. Kept out of the render so a redraw does not lose a selection. */
const selected = new Set<string>();

function paintBulk() {
  const bar = byId("taskBulk");
  if (!bar) return;
  bar.hidden = selected.size === 0;
  const count = byId("taskBulkCount");
  if (count) count.textContent = `${selected.size} selected`;
}

/**
 * The per-row and bulk actions on the list.
 *
 * Every one of them is something the detail modal could already do; the point is not new power but
 * fewer steps - thirty tasks cleared without opening thirty dialogs.
 */
function bindListActions() {
  const table = $("#page-tasks .task-table");
  if (!table) return;

  $$<HTMLInputElement>(".row-check", table).forEach((box) => {
    box.checked = selected.has(box.dataset.select!);
    on(box, "click", (e) => e.stopPropagation());       // ticking a row must not open it
    on(box, "change", () => {
      box.checked ? selected.add(box.dataset.select!) : selected.delete(box.dataset.select!);
      paintBulk();
    });
  });

  $$<HTMLElement>("[data-row]", table).forEach((b) => on(b, "click", async (e) => {
    e.stopPropagation();
    const id = b.dataset.id!;
    if (b.dataset.row === "done") { await completeTask(id); toast("Marked done"); return; }
    if (b.dataset.row === "schedule") { toast("Finding time…"); await scheduleTaskIds([id]); return; }
    const t = state.tasks.find((x) => x.id === id);
    if (t && confirm(`Delete ${t.title}?`)) { await deleteTask(id); selected.delete(id); paintBulk(); toast("Task deleted"); }
  }));

  on(byId("taskSelectAll"), "change", (e) => {
    const on_ = (e.target as HTMLInputElement).checked;
    $$<HTMLInputElement>(".row-check", table).forEach((b) => { b.checked = on_; on_ ? selected.add(b.dataset.select!) : selected.delete(b.dataset.select!); });
    paintBulk();
  });

  $$<HTMLElement>("[data-bulk]", table).forEach((b) => on(b, "click", async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (b.dataset.bulk === "done") { for (const id of ids) await completeTask(id); toast(`${ids.length} marked done`); }
    else if (b.dataset.bulk === "schedule") { toast("Finding time…"); await scheduleTaskIds(ids); }
    else if (confirm(`Delete ${ids.length} task${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) { for (const id of ids) await deleteTask(id); toast(`${ids.length} deleted`); }
    else return;
    selected.clear();
    paintBulk();
  }));

  paintBulk();
}

/**
 * Moving a card between columns.
 *
 * Shared by the pointer and the keyboard, because a board you can only operate by dragging is a
 * board some people cannot operate at all.
 */
async function moveTask(id: string, to: "open" | "scheduled" | "done") {
  const t = state.tasks.find((x) => x.id === id);
  if (!t) return;
  if (to === "done") { await completeTask(id); toast("Marked done"); return; }
  if (to === "open") {
    if (t.status === "open" && !t.scheduledStart) return;
    await updateTask(id, { status: "open", scheduledStart: null, scheduledEnd: null });
    toast("Back in your inbox");
    return;
  }
  if (t.scheduledStart) return;               // already planned; nothing to do
  toast("Finding time…");
  await scheduleTaskIds([id]);
}

/** Drag on the board, plus the arrow keys that do the same thing without a mouse. */
function setupBoardDrag() {
  const board = $("#page-tasks .task-board");
  if (!board) return;
  let dragging: string | null = null;

  on(board, "dragstart", (e) => {
    const card = (e.target as HTMLElement).closest<HTMLElement>(".board-task");
    if (!card) return;
    dragging = card.dataset.taskId ?? null;
    card.classList.add("dragging");
    card.setAttribute("aria-grabbed", "true");
    const dt = (e as DragEvent).dataTransfer;
    if (dt) { dt.effectAllowed = "move"; dt.setData("text/plain", dragging ?? ""); }
  });

  on(board, "dragend", () => {
    dragging = null;
    $$<HTMLElement>(".board-task.dragging", board).forEach((c) => { c.classList.remove("dragging"); c.setAttribute("aria-grabbed", "false"); });
    $$<HTMLElement>(".board-column.drop-target", board).forEach((c) => c.classList.remove("drop-target"));
  });

  // dragover must be cancelled or the browser refuses the drop outright.
  on(board, "dragover", (e) => {
    const col = (e.target as HTMLElement).closest<HTMLElement>(".board-column");
    if (!col || !dragging) return;
    e.preventDefault();
    (e as DragEvent).dataTransfer!.dropEffect = "move";
    $$<HTMLElement>(".board-column", board).forEach((c) => c.classList.toggle("drop-target", c === col));
  });

  on(board, "drop", async (e) => {
    const col = (e.target as HTMLElement).closest<HTMLElement>(".board-column");
    const id = dragging ?? (e as DragEvent).dataTransfer?.getData("text/plain");
    $$<HTMLElement>(".board-column.drop-target", board).forEach((c) => c.classList.remove("drop-target"));
    if (!col || !id) return;
    e.preventDefault();
    const to = col.dataset.drop as "open" | "scheduled" | "done" | undefined;
    if (to) await moveTask(id, to);
  });

  // The cards already take focus, so give the keyboard the same three moves.
  on(board, "keydown", async (e) => {
    const card = (e.target as HTMLElement).closest<HTMLElement>(".board-task");
    const key = (e as KeyboardEvent).key;
    if (!card || (key !== "ArrowLeft" && key !== "ArrowRight")) return;
    const order: ("open" | "scheduled" | "done")[] = ["open", "scheduled", "done"];
    const here = order.indexOf((card.dataset.status === "scheduled" ? "scheduled" : card.dataset.status === "done" ? "done" : "open"));
    const next = order[here + (key === "ArrowRight" ? 1 : -1)];
    if (!next) return;
    e.preventDefault();
    await moveTask(card.dataset.taskId!, next);
  });
}

export function setupTasks(getTasks: () => Task[]) {
  setupBoardDrag();
  on(byId("taskSearch"), "input", (e) => { search = (e.target as HTMLInputElement).value.trim(); renderTasks(getTasks()); });
  $$("[data-task-filter]").forEach((b) => on(b, "click", () => { $$("[data-task-filter]").forEach((x) => x.classList.remove("active")); b.classList.add("active"); filter = b.dataset.taskFilter!; renderTasks(getTasks()); }));
  $$("[data-task-view]").forEach((b) => on(b, "click", () => $$("[data-view-panel]").forEach((p) => p.classList.toggle("active", p.dataset.viewPanel === b.dataset.taskView))));
  on(byId("closeTaskDetail"), "click", closeTaskDetail);
  on(byId("taskDetailModal"), "click", (e) => e.target === byId("taskDetailModal") && closeTaskDetail());
  document.addEventListener("timo:escape", closeTaskDetail);
  const current = () => getTasks().find((x) => x.id === activeTaskId) ?? null;
  const readForm = (): Partial<Task> => {
    const due = (byId("taskDetailDue") as HTMLInputElement).value;
    return {
      title: (byId("taskDetailTitle") as HTMLTextAreaElement).value.trim() || "Untitled task",
      notes: (byId("taskDetailNotes") as HTMLTextAreaElement).value.trim(),
      project: byId<HTMLInputElement>("taskDetailProject")!.value.trim().slice(0, 40) || null,
      dueAt: due ? new Date(`${due}T17:00:00`) : null,
      priority: Number((byId("taskDetailPriority") as HTMLSelectElement).value) as Task["priority"],
      durationMin: Math.max(5, Number((byId("taskDetailEffort") as HTMLInputElement).value) || 30),
    };
  };
  ["taskDetailTitle", "taskDetailNotes"].forEach((id) => on(byId(id), "input", (e) => grow(e.currentTarget as HTMLTextAreaElement)));
  on(byId("taskDetailTitle"), "keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); (byId("taskDetailForm") as HTMLFormElement).requestSubmit(); } });
  on(byId("taskDetailForm"), "submit", async (e) => { e.preventDefault(); const t = current(); if (!t) return; await updateTask(t.id, readForm(), "user"); closeTaskDetail(); toast("Task updated"); });
  on(byId("completeDetailTask"), "click", async () => {
    const t = current(); if (!t) return;
    await completeTask(t.id, t.status !== "done");
    closeTaskDetail();
    toast(t.status !== "done" ? "Task completed" : "Task reopened");
  });
  on(byId("scheduleDetailTask"), "click", async () => { const t = current(); if (!t) return; await updateTask(t.id, { ...readForm(), status: t.status === "suggested" ? "open" : t.status }, "user"); closeTaskDetail(); await scheduleTaskIds([t.id]); });
  on(byId("moveTomorrowTask"), "click", async () => {
    const t = current(); if (!t) return;
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(17, 0, 0, 0);
    // Drop today's slot so the scheduler can place it again tomorrow; keep the event out of today's plan.
    await updateTask(t.id, { ...readForm(), dueAt: tomorrow, scheduledStart: null, scheduledEnd: null, eventId: null }, "user");
    if (t.eventId) await deleteEvent(t.eventId).catch(() => {});
    closeTaskDetail(); await scheduleTaskIds([t.id]);
  });
  on(byId("focusDetailTask"), "click", () => { const t = current(); if (!t) return; closeTaskDetail(); document.dispatchEvent(new CustomEvent("timo:focus-task", { detail: t.id })); goTo("focus"); });
  on(byId("taskDetailOpenSource"), "click", () => { const t = current(); if (!t?.meetingId) return; closeTaskDetail(); goTo("meetings"); openMeeting(t.meetingId); });
  on(byId("deleteDetailTask"), "click", async () => { const t = current(); if (!t) return; if (!confirm(`Delete ${t.title.slice(0, 60)}${t.title.length > 60 ? "…" : ""}?${t.scheduledStart ? " Its calendar block is removed too." : ""}`)) return; await deleteTask(t.id); closeTaskDetail(); toast("Task deleted"); });
}
